import type { ExamScore } from '@/types';

export interface SubjectTrend {
  subject: string;
  latest: number;
  delta: number | null;
  points: number[];
  recent: number;
}

export function parseScore(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = String(value).match(/\d+(\.\d+)?/);
  if (!match) return null;
  const num = Number(match[0]);
  return Number.isFinite(num) ? num : null;
}

function periodRank(s: ExamScore): number {
  if (s.exam_type === '모의고사') {
    const month = Number((s.month ?? '').replace(/[^\d]/g, '')) || 0;
    return month * 10;
  }
  return (s.semester === '2학기' ? 2 : 1) * 30;
}

export function buildSubjectTrends(scores: ExamScore[], limit = 3): SubjectTrend[] {
  const grouped = new Map<string, { score: number; order: number; seq: number }[]>();

  scores.forEach((s, index) => {
    const subject = (s.subject ?? '').trim();
    if (!subject) return;
    const value = parseScore(s.score);
    if (value === null) return;
    const year = Number(s.year) || 0;
    const order = year * 1000 + periodRank(s);
    const list = grouped.get(subject) ?? [];
    list.push({ score: value, order, seq: index });
    grouped.set(subject, list);
  });

  const trends: SubjectTrend[] = [];
  grouped.forEach((list, subject) => {
    list.sort((a, b) => a.order - b.order || a.seq - b.seq);
    const points = list.map((item) => item.score);
    const latest = points[points.length - 1];
    const prev = points.length > 1 ? points[points.length - 2] : null;
    trends.push({
      subject,
      latest,
      delta: prev === null ? null : latest - prev,
      points,
      recent: list[list.length - 1].order,
    });
  });

  trends.sort((a, b) => b.recent - a.recent || a.subject.localeCompare(b.subject));
  return trends.slice(0, limit);
}

export interface ExamScoreSession {
  key: string;
  examType: string;
  year: string;
  grade: string;
  semester: string;
  month: string | null;
  rows: ExamScore[];
  latest: number;
}

export function groupExamScores(scores: ExamScore[]): ExamScoreSession[] {
  const map = new Map<string, ExamScoreSession>();

  scores.forEach((s) => {
    const isMock = s.exam_type === '모의고사';
    const semester = isMock ? '' : s.semester;
    const month = isMock ? (s.month ?? '') : '';
    const key = [s.exam_type, s.year, s.grade, semester, month].join('|');

    let session = map.get(key);
    if (!session) {
      session = {
        key,
        examType: s.exam_type,
        year: s.year,
        grade: s.grade,
        semester,
        month: month || null,
        rows: [],
        latest: 0,
      };
      map.set(key, session);
    }
    session.rows.push(s);
    const time = s.created_at ? new Date(s.created_at).getTime() : 0;
    if (time > session.latest) session.latest = time;
  });

  const list = Array.from(map.values());
  list.forEach((session) => session.rows.sort((a, b) => a.subject.localeCompare(b.subject, 'ko')));
  list.sort((a, b) => {
    const ay = Number(a.year) || 0;
    const by = Number(b.year) || 0;
    if (ay !== by) return by - ay;
    return b.latest - a.latest;
  });

  return list;
}

export function buildExamTitle(
  session: Pick<ExamScoreSession, 'examType' | 'year' | 'grade' | 'semester' | 'month'>,
): string {
  if (session.examType === '모의고사') {
    return `${session.year} ${session.month ?? ''} 모의고사`.replace(/\s+/g, ' ').trim();
  }
  return [session.grade, session.semester, session.examType].filter(Boolean).join(' ');
}

export interface ExamComparisonRow {
  subject: string;
  prevScore: string;
  currScore: string;
  delta: number | null;
}

export function buildExamComparison(curr: ExamScore[], prev: ExamScore[]): ExamComparisonRow[] {
  const subjects: string[] = [];
  const seen = new Set<string>();
  [...curr, ...prev].forEach((r) => {
    const subject = (r.subject ?? '').trim();
    if (!subject || seen.has(subject)) return;
    seen.add(subject);
    subjects.push(subject);
  });
  subjects.sort((a, b) => a.localeCompare(b, 'ko'));

  const prevMap = new Map(prev.map((p) => [(p.subject ?? '').trim(), p] as const));
  const currMap = new Map(curr.map((c) => [(c.subject ?? '').trim(), c] as const));

  return subjects.map((subject) => {
    const prevScore = prevMap.get(subject)?.score ?? '';
    const currScore = currMap.get(subject)?.score ?? '';
    const prevNum = parseScore(prevScore);
    const currNum = parseScore(currScore);
    return {
      subject,
      prevScore: prevScore || '-',
      currScore: currScore || '-',
      delta: prevNum !== null && currNum !== null ? currNum - prevNum : null,
    };
  });
}